export type AccountState = 'None' | 'Invited' | 'InviteExpired' | 'Active' | 'Suspended'

export type UserRole = 'Owner' | 'Admin' | 'Teacher' | 'Parent' | 'Dancer'

export type StaffAccount = {
  staffId: string
  name: string

  /** The record behind that name, for the rename control (D-563). */
  firstName: string
  lastName: string
  displayName: string | null

  title: string | null
  email: string | null
  /** The address on her staff record, which is what the studio writes to (D-613). */
  staffEmail: string | null
  /** The number on her staff record, corrected beside the address (D-613). */
  staffPhone: string | null
  /**
   * Whether correcting her staff address moves her login with it, and so
   * signs her out everywhere (D-613).
   *
   * Answered by the server, with the same function the handler uses. Working
   * it out here from the two addresses is a second implementation of one
   * question, and it drifted on a difference of case.
   */
  loginMoves: boolean
  staffIsActive: boolean
  accountId: string | null
  role: UserRole | null
  state: AccountState
  inviteExpiresAt: string | null
  lastSignInAt: string | null
  grantedCapabilities: string[]
  effectiveCapabilities: string[]
  /** The parent record this login is also linked to, once Tonya has said so. */
  guardianId: string | null
  guardianName: string | null
  /**
   * A likely match by email address — offered, never acted on.
   *
   * Silent unless the address is unique on both sides, so a shared office
   * address suggests nothing rather than naming the wrong colleague (D-305).
   */
  suggestedGuardianId: string | null
  suggestedGuardianName: string | null
  /**
   * The dancer this person also is, when somebody has said so (D-604).
   *
   * Not gated on having a login, which the family link above is. That one
   * lives on the account because it decides what a login may see about
   * itself; this is person to person on the staff record, and 43 of the 48
   * staff have no account at all.
   */
  dancerId: string | null
  dancerName: string | null
  /**
   * The day she stopped working here, once somebody has said so (D-605).
   *
   * Null on everybody still on the staff. Carried rather than derived from
   * `staffIsActive` so the row can say *when*, which is what makes the list
   * something Tonya trusts rather than one she has to remember the reasons
   * behind.
   */
  endedOn: string | null
}

export type GuardianOption = {
  id: string
  name: string
  email: string | null
  household: string
  alreadyLinked: boolean
}

export type CapabilityInfo = {
  name: string
  group: string
  ownerOnlyByDefault: boolean
  inRoleByDefault: boolean
}

/**
 * What each state means for the person reading the screen.
 *
 * Phrased as the situation, not the mechanism — "never invited" rather than
 * "no account", because the reader is deciding what to do next, not auditing
 * the data model.
 */
export const STATE_LABELS: Record<AccountState, string> = {
  None: 'Never invited',
  Invited: 'Invited',
  InviteExpired: 'Invite expired',
  Active: 'Can sign in',
  Suspended: 'Suspended',
}

export const ROLES: UserRole[] = ['Owner', 'Admin', 'Teacher']

async function send(url: string, method: string, body?: unknown): Promise<void> {
  const res = await fetch(url, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!res.ok) {
    // Every refusal here is a considered one with something to say — the last
    // owner, your own account, a capability you do not hold. Surfacing the
    // detail is the whole point.
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Request failed (${res.status})`)
  }
}

export async function fetchStaffAccounts(): Promise<StaffAccount[]> {
  const res = await fetch('/api/admin/accounts')
  if (!res.ok) throw new Error(`Could not load accounts (${res.status})`)
  return res.json()
}

export async function fetchCapabilities(): Promise<CapabilityInfo[]> {
  const res = await fetch('/api/admin/capabilities')
  if (!res.ok) throw new Error(`Could not load capabilities (${res.status})`)
  return res.json()
}

export type InviteResult = {
  accountId: string
  email: string
  expiresAt: string
  /** Development only — the server never returns this in production. */
  inviteLink: string | null
  emailed: boolean
  emailProblem: string | null
}

export async function invite(input: {
  email: string
  staffId: string
  role: UserRole
}): Promise<InviteResult> {
  const res = await fetch('/api/auth/invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)

    /*
     * <b>A refusal that carries a way out</b> (D-604). A parent who signed
     * herself up already has a login, so there is nothing to invite — and the
     * screen used to report only "that address already has an active login",
     * which sent Tonya looking for a password reset that would not have helped.
     * The server names the account; this hands it to the screen so it can offer
     * the link rather than a dead end.
     */
    if (problem?.accountId) {
      throw new ExistingLogin(
        problem.detail ?? 'She already has a login.',
        problem.accountId,
        problem.accountName ?? 'that login',
      )
    }

    throw new Error(problem?.detail ?? problem?.title ?? `Could not invite (${res.status})`)
  }

  return res.json()
}

/** The invite could not happen because this person already signs in (D-604). */
export class ExistingLogin extends Error {
  // Plain fields rather than constructor parameter properties: the build runs
  // with `erasableSyntaxOnly`, which forbids the shorthand.
  accountId: string
  accountName: string

  constructor(message: string, accountId: string, accountName: string) {
    super(message)
    this.name = 'ExistingLogin'
    this.accountId = accountId
    this.accountName = accountName
  }
}

export async function searchGuardians(query: string): Promise<GuardianOption[]> {
  const res = await fetch(`/api/admin/guardians?q=${encodeURIComponent(query)}`)
  if (!res.ok) throw new Error(`Could not load parents (${res.status})`)
  return res.json()
}

/** Says that this member of staff is also a parent here (D-305). */
export const linkFamily = (staffId: string, guardianId: string) =>
  send(`/api/admin/staff/${staffId}/family`, 'POST', { guardianId })

export const unlinkFamily = (staffId: string) =>
  send(`/api/admin/staff/${staffId}/family`, 'DELETE')

/** A dancer this member of staff might also be, offered and never acted on. */
export type DancerOption = {
  id: string
  name: string
  email: string | null
  /** Same first and last name. A hint, ordered first, never applied. */
  likelyMatch: boolean
}

export async function dancerCandidates(staffId: string, q = ''): Promise<DancerOption[]> {
  const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''

  const res = await fetch(`/api/admin/staff/${staffId}/dancer-candidates${params}`)
  if (!res.ok) throw new Error(`Could not load the dancers (${res.status})`)
  return res.json()
}

/** Says that this member of staff is also a dancer here (D-604). */
export const linkDancer = (staffId: string, dancerId: string) =>
  send(`/api/admin/staff/${staffId}/dancer`, 'POST', { dancerId })

export const unlinkDancer = (staffId: string) =>
  send(`/api/admin/staff/${staffId}/dancer`, 'DELETE')

/**
 * She no longer works here (D-605).
 *
 * Deactivated, never deleted: a class taught in October still names whoever
 * taught it. It takes her out of the cover list, the ask recipients, the
 * search, the PIN pad and the class instructor lists, and suspends her login
 * if she has one — one act, because two switches for one fact is how a
 * departed teacher keeps a working password.
 *
 * The date defaults to today on the server. Passing one is for tidying up
 * months after the fact, which is the common case.
 */
export async function retireStaff(
  staffId: string,
  endedOn?: string,
): Promise<{ suspendedALogin: boolean; keptALogin: boolean }> {
  const res = await fetch(`/api/admin/staff/${staffId}/departure`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endedOn: endedOn ?? null }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Request failed (${res.status})`)
  }

  return res.json()
}

/** She is back, and so is her login if she had one. */
export const unretireStaff = (staffId: string) =>
  send(`/api/admin/staff/${staffId}/departure`, 'DELETE')

/**
 * "March 2026", for a leaving date on a row (D-605).
 *
 * The month is the useful grain — "did she leave before or after the thing I
 * am looking at" — and a day would ask the reader to care about a date that is
 * often the day somebody got round to tidying the list rather than her last
 * lesson.
 *
 * Parsed with an explicit midnight, never `new Date(iso)`, which reads a bare
 * date as UTC and can land on the previous month west of Greenwich (D-68).
 */
export function monthAndYear(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

/**
 * Attaches a staff record to a login the person already has (D-604).
 *
 * The case is a parent who signed herself up before anybody invited her staff
 * record. She keeps her own password; nothing is emailed. The account id comes
 * from the invite refusal, never from matching an address.
 *
 * No role: a staff link already carries the teaching capabilities whatever the
 * role says (D-538), so sending one would move a label and nothing else.
 */
export const linkExistingLogin = (staffId: string, accountId: string) =>
  send(`/api/admin/staff/${staffId}/login`, 'POST', { accountId })

export const changeRole = (accountId: string, role: UserRole) =>
  send(`/api/admin/accounts/${accountId}/role`, 'POST', { role })

export const grant = (accountId: string, capability: string) =>
  send(`/api/admin/accounts/${accountId}/grants`, 'POST', { capability })

export const revoke = (accountId: string, capability: string) =>
  send(`/api/admin/accounts/${accountId}/grants/${capability}`, 'DELETE')

export const suspend = (accountId: string) =>
  send(`/api/admin/accounts/${accountId}/suspend`, 'POST')

export const restore = (accountId: string) =>
  send(`/api/admin/accounts/${accountId}/restore`, 'POST')

/**
 * How many people still cannot sign in.
 *
 * The number the screen exists to drive to zero. Counts only staff who are
 * still employed — a departed teacher without a login is not a gap, and
 * counting them would make the number permanently non-zero and therefore
 * ignorable.
 */
export function stillLockedOut(accounts: StaffAccount[]): number {
  return accounts.filter(
    (a) => a.staffIsActive && (a.state === 'None' || a.state === 'InviteExpired'),
  ).length
}

/**
 * Capabilities this account holds *because of a grant*, not its role.
 *
 * The distinction matters on screen: a role capability cannot be revoked
 * without changing the role, so offering a revoke button for it would be a
 * control that does nothing.
 */
export function grantedOnly(account: StaffAccount): string[] {
  return account.grantedCapabilities
}

/** Groups the catalog for display, preserving the server's ordering. */
export function byGroup(capabilities: CapabilityInfo[]): { group: string; items: CapabilityInfo[] }[] {
  const groups: { group: string; items: CapabilityInfo[] }[] = []

  for (const capability of capabilities) {
    const existing = groups.find((g) => g.group === capability.group)
    if (existing) existing.items.push(capability)
    else groups.push({ group: capability.group, items: [capability] })
  }

  return groups
}

/** `ViewPayRates` -> `View pay rates`. */
export function humanise(capability: string): string {
  const spaced = capability.replace(/(?<!^)([A-Z])/g, ' $1').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Filters the list by name, email, or state.
 *
 * 48 staff is too many to scan and too few to paginate, so a single box that
 * matches whatever the reader happens to type is the right shape.
 */
export function filterAccounts(accounts: StaffAccount[], query: string): StaffAccount[] {
  const q = query.trim().toLowerCase()
  if (!q) return accounts

  return accounts.filter(
    (a) =>
      a.name.toLowerCase().includes(q) ||
      (a.email ?? '').toLowerCase().includes(q) ||
      (a.title ?? '').toLowerCase().includes(q) ||
      STATE_LABELS[a.state].toLowerCase().includes(q) ||
      (a.role ?? '').toLowerCase().includes(q),
  )
}

/**
 * A staff record, for a person who may well have no login (D-534).
 *
 * Deliberately not {@link StaffAccount}: that is about the login somebody does
 * or does not have, and a person created here certainly has none.
 */
export type StaffRecord = {
  id: string
  firstName: string
  lastName: string
  /** What the staff list calls her, which is not always her name. */
  name: string
  email: string | null
  isActive: boolean
  /** Set when she is also a dancer here (D-535). */
  dancerId: string | null
}

/**
 * Adds a member of staff.
 *
 * **This creates a person, not a login.** 43 of the 48 staff have none;
 * inviting is a separate act. Until this existed, every teacher the studio had
 * arrived by Jackrabbit import and somebody hired mid-season could not be
 * entered at all.
 */
export async function addStaff(input: {
  firstName: string
  lastName: string
  email: string | null
  mobilePhone: string | null
}): Promise<StaffRecord> {
  const res = await fetch('/api/admin/staff', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)

    // The server names the person already holding the address; that sentence
    // is the useful half and it must survive to the screen.
    throw new Error(problem?.detail ?? problem?.title ?? `Could not add her (${res.status})`)
  }

  return res.json()
}

/** A staff record a dancer might already be, offered and never acted on. */
export type StaffCandidate = {
  id: string
  name: string
  email: string | null
  /** Same first and last name. A hint, ordered first, never applied. */
  likelyMatch: boolean
}

export async function fetchStaffCandidates(dancerId: string): Promise<StaffCandidate[]> {
  const res = await fetch(`/api/admin/dancers/${dancerId}/staff-candidates`)
  if (!res.ok) throw new Error(`Could not load the staff list (${res.status})`)
  return res.json()
}

/**
 * Gives a dancer a staff record, or says she is one that already exists.
 *
 * **Exactly one of the two, never both and never neither** (D-535). Naming a
 * record is Tonya saying *she is that person*; supplying an address is Tonya
 * saying *she is nobody on this list yet*. An earlier version guessed between
 * them by matching names, and a changed surname silently made a second Ava.
 */
export async function makeDancerStaff(
  dancerId: string,
  input: { staffId: string } | { email: string; mobilePhone: string | null },
): Promise<StaffRecord> {
  const res = await fetch(`/api/admin/dancers/${dancerId}/staff-record`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not do that (${res.status})`)
  }

  return res.json()
}

/**
 * Corrects the name a member of staff is known by (D-563).
 *
 * **Nothing could do this before.** A name arrived from a Jackrabbit import,
 * from the promote-a-dancer door, or from the owner bootstrap — which invents
 * *Studio Owner* when the studio's first account is claimed before its first
 * import — and then stood for ever, on every dropdown in the app.
 *
 * The display name goes with it, because that is what the lists actually read:
 * correcting one and not the other fixes the name everywhere except the screen
 * somebody was looking at.
 */
/**
 * Corrects how the studio reaches a member of staff (D-613).
 *
 * `asked`: *"we also need to allow for the editing of emails for staff, one
 * contacted us and we can't change it."* The domain could always do it and the
 * importer does it on every run; nothing let a person do it.
 *
 * **The number travels with the address**, because saving marks the pair as the
 * studio's and the next import leaves the pair alone — sending only the address
 * would freeze whatever number was last imported, on a field nothing else can
 * edit.
 *
 * **Her login moves with it where the login *was* that address**, and she keeps
 * her password — so she signs in with the new one rather than registering
 * again. That signs her out everywhere, which is why the caller has to say so
 * before asking, and `signedOut` lets it say so afterwards too. A teacher who
 * is also a parent may sign in as her family address instead (D-604); that
 * login is hers and is left alone.
 *
 * **A separate call from the rename**, because an address is not a label: it
 * decides where an invitation lands, it may be a login, and taking one that
 * belongs to somebody else is refused in words.
 */
export async function setStaffContact(
  staffId: string,
  contact: { email: string | null; mobilePhone: string | null },
): Promise<{ signedOut: boolean }> {
  const res = await fetch(`/api/admin/staff/${staffId}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(contact),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not change that (${res.status})`)
  }

  return res.json()
}

export async function renameStaff(
  staffId: string,
  name: { firstName: string; lastName: string; displayName: string | null },
): Promise<StaffRecord> {
  const res = await fetch(`/api/admin/staff/${staffId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(name),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not rename that (${res.status})`)
  }

  return res.json()
}
